import { Table } from "antd"
import { EuroCircleOutlined, TeamOutlined } from "@ant-design/icons"
import { ReactElement, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { BillingAddress } from "../queries"
import { BillingSpaceTableWrapper } from "../styles"
import {
    buildMetricRows, formatAmount, formatPeriod, MetricCategoryKey, MetricRow, sortSnapshotsByPeriod
} from "./billingUtils"

const ROW_TYPE_SECTION = 'section' as const
const ROW_TYPE_METRIC = 'metric' as const

type SectionTableRow = {
    key: string
    rowType: typeof ROW_TYPE_SECTION
    label: string
    categoryKey: MetricCategoryKey
}

type MetricTableRow = {
    key: string
    rowType: typeof ROW_TYPE_METRIC
    label: string
    [snapshotId: string]: number | string
}

type TableRow = SectionTableRow | MetricTableRow

const CATEGORY_ICONS: Partial<Record<MetricCategoryKey, ReactElement>> = {
    'client-volume': <TeamOutlined />,
    'commission': <EuroCircleOutlined />
}

export default function BillingDataTable({
    billingAddress
}: {
    billingAddress: BillingAddress
}) {
    const { t, i18n } = useTranslation()

    const snapshots = useMemo(
        () => sortSnapshotsByPeriod(billingAddress.retroCommissionSnapshots),
        [billingAddress.retroCommissionSnapshots]
    )
    const metricRows = useMemo(
        () => buildMetricRows(billingAddress, t),
        [billingAddress, t]
    )

    const metricRowByKey = useMemo(
        () => new Map<string, MetricRow>(metricRows.map((m) => [m.key, m])),
        [metricRows]
    )

    const tableRows = useMemo(() => {
        const rows: TableRow[] = []
        const seenCategories = new Set<string>()

        metricRows.forEach((metric) => {
            if (!metric.noSection && !seenCategories.has(metric.category)) {
                seenCategories.add(metric.category)
                rows.push({
                    key: `${metric.category}-section`,
                    rowType: ROW_TYPE_SECTION,
                    label: metric.category,
                    categoryKey: metric.categoryKey
                })
            }

            const metricRow: MetricTableRow = {
                key: metric.key,
                rowType: ROW_TYPE_METRIC,
                label: metric.label
            }

            if (metric.getValue) {
                snapshots.forEach((snapshot) => {
                    metricRow[String(snapshot.id)] = metric.getValue!(snapshot)
                })
            }

            rows.push(metricRow)
        })

        return rows
    }, [metricRows, snapshots])

    const columns = useMemo(() => {
        const monthColumns = snapshots.map((snapshot) => ({
            key: String(snapshot.id),
            dataIndex: String(snapshot.id),
            align: 'right' as const,
            className: 'month-column',
            width: 110,
            title: formatPeriod(snapshot.periodStart, i18n.language),
            render: (value: number, row: TableRow) => {
                if (row.rowType !== ROW_TYPE_METRIC) return null
                const metric = metricRowByKey.get(row.key)
                if (!metric?.getValue) return '-'
                return metric.valueType === 'currency' ? formatAmount(value || 0, i18n.language) : value || 0
            }
        }))

        return [
            {
                key: 'label',
                title: t('facturation.table.category-and-indicators'),
                dataIndex: 'label',
                fixed: 'left' as const,
                className: 'label-column',
                render: (value: string, row: TableRow) => {
                    if (row.rowType === ROW_TYPE_SECTION) {
                        const icon = CATEGORY_ICONS[row.categoryKey]
                        return <span className="section-title">{icon}<span>{value}</span></span>
                    }
                    return value
                }
            },
            ...monthColumns
        ]
    }, [snapshots, metricRowByKey, i18n.language, t])

    const rowClassName = (row: TableRow) => {
        if (row.rowType === ROW_TYPE_SECTION) return 'section-row'
        if (row.key === 'commissionTotal') return 'metric-row total-row'
        return 'metric-row'
    }

    return (
        <BillingSpaceTableWrapper>
            <Table
                bordered={false}
                pagination={false}
                rowKey="key"
                dataSource={tableRows}
                columns={columns}
                size="middle"
                sticky
                scroll={{ x: 'max-content' }}
                rowClassName={rowClassName}
            />
        </BillingSpaceTableWrapper>
    )
}
